import { FocusMonitor } from '@angular/cdk/a11y';
import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Optional, Output, Self } from '@angular/core';
import { UntypedFormBuilder, NgControl } from '@angular/forms';
import { SafeHtml } from '@angular/platform-browser';
import { Subject } from 'rxjs';
import { takeWhile } from 'rxjs/operators';
import { FdFieldBaseComponent } from '../fd-field/fd-field.base-component';
import { InputType } from '../fd-input/fd-input.component';
import { MaskConfig, MaskPatterns } from '../mask.types';

@Component({
    selector: 'fd-search-bar',
    templateUrl: './fd-search-bar.component.html',
    styleUrls: ['./fd-search-bar.component.scss'],
    standalone: false
})
export class FdSearchBarComponent extends FdFieldBaseComponent implements OnInit, OnDestroy {
  get required(): boolean {
    return false;
  }

  get hasIcon(): boolean {
    return this.field && !!this.field.iconHTML;
  }
  public static nextId = 0;
  public alive = true;

  @Input()
  public field: FdInputConfig;

  @Output()
  public input = new EventEmitter<string>();

  @Output()
  public blur = new EventEmitter<string>();

  public stateChanges = new Subject<void>();
  public focused = false;
  public errorState = false;
  public controlType = 'fd-input';
  public describedBy = '';
  public maskExpression = '';
  public resolvedPatterns?: MaskPatterns;

  constructor(
    formBuilder: UntypedFormBuilder,
    private focusMonitor: FocusMonitor,
    private elementRef: ElementRef<HTMLElement>,
    @Optional() @Self() public ngControl: NgControl
  ) {
    super();

    focusMonitor
      .monitor(elementRef, true)
      .pipe(takeWhile(() => this.alive))
      .subscribe((origin) => {
        if (this.focused && !origin) {
          this.onTouched();
        }
        this.focused = !!origin;
        this.stateChanges.next();
      });

    if (this.ngControl !== null) {
      this.ngControl.valueAccessor = this;
    }
  }
  public onChange = (_: any) => {};
  public onTouched = () => {};

  public ngOnDestroy() {
    this.alive = false;
    this.stateChanges.complete();
    this.focusMonitor.stopMonitoring(this.elementRef);
  }

  public setDescribedByIds(ids: string[]) {
    this.describedBy = ids.join(' ');
  }

  public onContainerClick(event: MouseEvent) {
    if ((event.target as Element).tagName.toLowerCase() !== 'input') {
      this.elementRef.nativeElement.querySelector('input')!.focus();
    }
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public handleBlur(event: Event): void {
    if (this.blur) {
      const inputValue = (event.target as HTMLInputElement).value;
      this.blur.emit(inputValue.replace(/\D+/g, ''));
    }
    event.stopPropagation();
  }

  public handleChange(event: Event): void {
    this.resolveMask((event.target as HTMLInputElement).value);

    if (this.input) {
      const inputValue = (event.target as HTMLInputElement).value;
      this.input.emit(inputValue);
    }
    event.stopPropagation();
  }

  public ngOnInit() {
    this.resolveMask();
  }

  private resolveMask(value: string = ''): void {
    if (typeof this.field?.mask === 'function') {
      this.maskExpression = this.field.mask(value);
      this.resolvedPatterns = this.field.mask.patterns;
      return;
    }

    this.maskExpression = this.field?.mask || '';
    this.resolvedPatterns = undefined;
  }
}

export interface FdInputConfig {
  controlName: string;
  label: string;
  hint?: string;
  iconHTML?: SafeHtml;
  maxLength?: number;
  disabled?: boolean;
  type?: InputType;
  mask?: MaskConfig;
  maskCharsReplace?: RegExp;
  messages?: { [key: string]: string };
  error?: boolean;
}
